import { Base } from '@wilson/base';
import { VacationStatus } from '../absences/vacation-status';
import { Address } from '../addresses/address';
import { UserRole } from '../authorization/role';
import type { Contract } from '../contracts/contract';
import { GeoLocation } from '../locations/geoLocation';
import {
  AuthenticatedUserOrganizationalUnit,
  OrganizationalUnit,
} from '../organizational-units/organizationalUnit';
import { ShiftPayrollAcceptanceStatus } from '../payroll/payroll-enums';
import { UserVacation } from './user-vacation.interface';
import { Label } from '../labels/labels';

export const NO_USER_CONSTANT = 'no-user';

export interface User extends Base {
  personnelId: string | null;
  payrollId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string | null;
  birthDate: string | null;
  profileImageUrl: string | null;
  approvedToWork: boolean;
  isOnline: boolean;
  isConfirmed: boolean;
  canAccessWilson: boolean;
  lastLogin: Date | null;
  organizationalUnit?: OrganizationalUnit;
  organizationalUnitId: string;
  address?: Address;
  addressId: string | null;
  contract?: Contract;
  contractId: string;
  roleIds?: string[];
  userRoles?: UserRole[];
  autoSkipActivity: boolean;
  about: string | null;
  comment: string | null;
  userSetting?: UserSetting;
  accessibleOrganizationalUnits?: OrganizationalUnit[]; // TODO - Currently complete object due to cascade true in BE
  homeLocation?: GeoLocation | null;
  homeLocationId?: string | null;
  publicHolidaysRegion?: string[];
  authOption: UserAuthOption;
  professionIds: string[];
  labels?: Label[];
  userPayrollMonthlyClosures?: UserPayrollMonthlyClosure[];
  managerId: string | null;
  isAbsenceMasterDecider: boolean;
}

export interface ResolvedUser extends User {
  organizationalUnit: OrganizationalUnit;
  contract: Contract;
}

/**
 * User shape returned by the API when loading with
 * InitializeUserWithRootOrgUnit relations (accessibleOrganizationalUnits,
 * contract + contract.activityCategoryContracts, organizationalUnit +
 * mobileSettings/root/localizationSetting, userRoles, userSetting).
 */
export interface AuthenticatedUser
  extends Omit<
    User,
    | 'accessibleOrganizationalUnits'
    | 'contract'
    | 'organizationalUnit'
    | 'userRoles'
    | 'userSetting'
  > {
  accessibleOrganizationalUnits: OrganizationalUnit[];
  contract: Contract;
  organizationalUnit: AuthenticatedUserOrganizationalUnit;
  userRoles: UserRole[];
  userSetting: UserSetting;
}

export enum TimeSheetStatus {
  NotSent = 'NOT_SENT',
  Sent = 'SENT',
  InProgress = 'IN_PROGRESS',
}

export interface UserWithShiftAcceptanceStatusDto extends User {
  numPublishedShifts: number;
  numAcceptedShifts: number;
  numPaidShifts: number;
  acceptanceStatus: ShiftPayrollAcceptanceStatus;
  timeSheetFilePath?: string;
  userPayrollMonthlyClosures?: UserPayrollMonthlyClosure[];
  timeSheetStatus: TimeSheetStatus;
}

export type UserWithShiftAcceptanceStatus = UserWithShiftAcceptanceStatusDto;

export interface UserWithUserWorkingTimeModel extends User {
  isAdded: boolean;
  activeFrom: Date | null;
}

export interface UserWithPayrollTimeSheet {
  startDate: string;
  endDate: string;
  userIds: string[];
}

export interface UserWithVacationStatus extends User {
  vacationStatus: UserVacation | VacationStatus;
}

export interface ChatUsers extends User {
  isChecked: boolean;
}
export interface UserSetting extends Base {
  canDoSelfDispatching: boolean;
  canViewServiceActivitiesOnMobile: boolean;
  userId: string;
}

export enum UserAuthOption {
  Wilson = 'wilson',
  Azure = 'azure',
}

export interface UserPayrollMonthlyClosure {
  yearAndMonth: string;
  status: MonthlyClosureStatusEnum;
  userId: string;
}

export enum MonthlyClosureStatusEnum {
  InProgress = 'in-progress',
  SuccessfullyClosed = 'successfully-closed',
  Failed = 'failed',
}
